import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {Checkbox, Col, Form, Input, message, Radio, Row} from "antd";
import {ScheduleType} from "../../../../../proto/generated/api_entities_pb";
import dayjs, {Dayjs} from "dayjs";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {clearCreateEvent, createEvent} from "../../../../../store/reducers/scheduleReducer/scheduleReducer";
import {DateAndTimeRangePicker} from "../../../../../components/UI/AntCustom/DateAndTimeRangePicker";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import WarningIcon from "../../../../../media/icons/warning.svg?react";
import PlaneGreyIcon from "../../../../../media/icons/plane-grey.svg?react";
import {
  fetchAirports,
  fetchInitialAirportForSelect
} from "../../../../../utils/helpers/selectDataFetchers/fetchAirports";
import {fetchOwnPlanes} from "../../../../../utils/helpers/selectDataFetchers/fetchOwnPlanes";
import {useAirportFilter} from "../../../../../utils/hooks/useAirportFilter";
import {renderSuccessModal} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonUnitMessages} from "../../../../../intl/commonMessages/CommonUnitMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {API} from "../../../../../API/API";
import {onFinishTrimmed} from "../../../../../utils/helpers/formHelpers/onFinishTrimmed";
import {AirportApiGetAirportRequest} from "../../../../../proto/generated/api_entities_pb";
import {mapAirportEntityToIAirport} from "../../../../../store/reducers/fleetReducer/fleetMappers";
import {dayjsToUTCDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";
import {IFreighterOrderRequest} from "../../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducerTypes";
import {IOrderRequestPlane} from "../../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducerTypes";


const ScheduleCreateFromOrderModalMessages = defineMessages({
  success: {
    id: "scheduleCreateFromOrderModal.success",
    defaultMessage: "The new event has been successfully added to schedule",
  },
  title: {
    id: "scheduleCreateFromOrderModal.title",
    defaultMessage: "Add to schedule",
  }
})

interface IScheduleCreateFormData {
  planeId: number;
  eventType: ScheduleType;
  dateRange: [Dayjs, Dayjs];
  airportFromId: number;
  airportToId?: number;
  eventName?: string;
  refuelingCount?: number;
  pax?: number;
  isPublic?: boolean;
}

interface IScheduleCreateFromOrderModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  orderRequest: IFreighterOrderRequest;
  approvedPlane: IOrderRequestPlane;
}

export const ScheduleCreateFromOrderModal: FC<IScheduleCreateFromOrderModal> = ({
  isModalVisible,
  setIsModalVisible,
  orderRequest,
  approvedPlane
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [eventTypeRadioValue, setEventTypeRadioValue] = useState<ScheduleType>(ScheduleType.ORDER_REQUEST);
  const [airportFromOffset, setAirportFromOffset] = useState<number | null>(null);
  const [airportToOffset, setAirportToOffset] = useState<number | null>(null);
  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.schedule.create);

  // Вычисляем начальные значения из заказа
  const getInitialValues = (): Partial<IScheduleCreateFormData> => {
    const firstRoute = orderRequest.orderRouteOrderRequests[0]?.orderRoute;
    const lastRoute = orderRequest.orderRouteOrderRequests[orderRequest.orderRouteOrderRequests.length - 1]?.orderRoute;
    
    if (!firstRoute) {
      return {};
    }

    // Дата отправления из первого маршрута
    const departureDate = firstRoute.departureDate ? dayjs(firstRoute.departureDate) : undefined;
    
    // Дата прибытия - из последнего маршрута или вычисляем из flightDurationMinutes
    let arrivalDate: Dayjs | undefined;
    if (approvedPlane.flightDurationMinutes && departureDate) {
      arrivalDate = departureDate.add(approvedPlane.flightDurationMinutes, 'minute');
    } else if (lastRoute?.departureDate) {
      arrivalDate = dayjs(lastRoute.departureDate);
    } else if (orderRequest.orderRouteOrderRequests[0]?.arrivalDate) {
      arrivalDate = dayjs(orderRequest.orderRouteOrderRequests[0].arrivalDate);
    }

    // Количество пассажиров - максимальное из всех маршрутов
    const pax = Math.max(...orderRequest.orderRouteOrderRequests.map(route => route.orderRoute.pax ?? 0), 0);

    // Количество дозаправок из approvedPlane или сумма по маршрутам
    const refuelingCount = approvedPlane.refuelingCount ?? 
      orderRequest.orderRouteOrderRequests.reduce((sum, route) => sum + (route.technicalStopCount ?? 0), 0);

    return {
      planeId: approvedPlane.planeId,
      eventType: ScheduleType.ORDER_REQUEST,
      dateRange: departureDate && arrivalDate ? [departureDate, arrivalDate] : undefined,
      airportFromId: firstRoute.airportFrom.id,
      airportToId: lastRoute?.airportTo?.id,
      refuelingCount: refuelingCount > 0 ? refuelingCount : undefined,
      pax: pax > 0 ? pax : undefined,
      isPublic: false, // По умолчанию приватный
    };
  };

  useEffect(() => {
    if (isModalVisible) {
      const initialValues = getInitialValues();
      form.setFieldsValue(initialValues);
      setEventTypeRadioValue(initialValues.eventType || ScheduleType.ORDER_REQUEST);
      
      // Загружаем офсеты для аэропортов
      if (initialValues.airportFromId) {
        loadAirportOffset(initialValues.airportFromId, 'from');
      }
      if (initialValues.airportToId) {
        loadAirportOffset(initialValues.airportToId, 'to');
      }
    } else {
      // Сбрасываем форму при закрытии
      form.resetFields();
      setAirportFromOffset(null);
      setAirportToOffset(null);
    }
  }, [isModalVisible, orderRequest, approvedPlane]);

  const loadAirportOffset = async (airportId: number, type: 'from' | 'to') => {
    try {
      const req = new AirportApiGetAirportRequest().setAirportid(airportId);
      const airportEntity = (await API.getAirportById(req)).getAirport();
      if (airportEntity) {
        const airport = mapAirportEntityToIAirport(airportEntity);
        if (type === 'from') {
          setAirportFromOffset(airport.timezone?.offset ?? null);
        } else {
          setAirportToOffset(airport.timezone?.offset ?? null);
        }
      }
    } catch (error) {
      console.error(`Failed to load airport ${type}:`, error);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearCreateEvent());
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess) {
        renderSuccessModal(intl.formatMessage(ScheduleCreateFromOrderModalMessages.success), () => {
          setIsModalVisible(false);
          form.resetFields();
        });
      }
      if (errorMessage) {
        message.error(errorMessage);
      }
    }
  }, [isSuccess, errorMessage, isLoading])

  const handleFormValuesChange = async (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === "eventType") {
      const value = changedValues[fieldName];
      setEventTypeRadioValue(value)
    } else if (["airportFromId", "airportToId"].includes(fieldName)) {
      const airportFromId = fieldName === 'airportFromId' ? changedValues[fieldName] : form.getFieldValue('airportFromId');
      const airportToId = fieldName === 'airportToId' ? changedValues[fieldName] : form.getFieldValue('airportToId');

      if (airportFromId) {
        await loadAirportOffset(airportFromId, 'from');
      } else {
        setAirportFromOffset(null);
      }

      if (airportToId) {
        await loadAirportOffset(airportToId, 'to');
      } else {
        setAirportToOffset(null);
      }
    }
  }

  const onFinish = (values: IScheduleCreateFormData) => {
    dispatch(createEvent({
      eventType: values.eventType,
      eventName: values.eventName,
      planeId: values.planeId,
      dateFrom: dayjsToUTCDate(values.dateRange[0]),
      dateTo: dayjsToUTCDate(values.dateRange[1]),
      airportFromId: values.airportFromId,
      airportToId: values.airportToId,
      refuelingCount: values.refuelingCount,
      pax: values.pax,
      isPublic: values.isPublic,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const getInitialAirportFromCallback = () => {
    const currentAirportFromId = form.getFieldValue('airportFromId');
    if (!currentAirportFromId) {
      return Promise.resolve(undefined);
    }
    return fetchInitialAirportForSelect(currentAirportFromId);
  }

  const getInitialAirportToCallback = () => {
    const currentAirportToId = form.getFieldValue('airportToId');
    if (!currentAirportToId) {
      return Promise.resolve(undefined);
    }
    return fetchInitialAirportForSelect(currentAirportToId);
  }

  const fromAirportFilter = useAirportFilter(form, 'airportToId');
  const toAirportFilter = useAirportFilter(form, 'airportFromId');

  return (
    <CustomModal
      title={intl.formatMessage(ScheduleCreateFromOrderModalMessages.title)}
      open={isModalVisible}
      onCancel={() => {
        setIsModalVisible(false);
        form.resetFields();
      }}
      centered
      footer={null}
      width={700}
    >
      <Form
        form={form}
        onFinish={(values) => onFinishTrimmed(values, onFinish)}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
        onValuesChange={handleFormValuesChange}
      >
        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.planeLabel)}
            name={'planeId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <DebounceSelect
              fetchOptions={fetchOwnPlanes}
              placeholder={intl.formatMessage(CommonFormMessages.planePlaceholder)}
            />
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.eventTypeLabel)}
            name={'eventType'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <Radio.Group>
              <Radio value={ScheduleType.ORDER_REQUEST}>{intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio)}</Radio>
              <Radio value={ScheduleType.EMPTY_LEG}>{intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio)}</Radio>
            </Radio.Group>
          </CustomFieldInput>
        </Col>

        {(eventTypeRadioValue === ScheduleType.ORDER_REQUEST || eventTypeRadioValue === ScheduleType.EMPTY_LEG) &&
          <>
            <Col flex={1}>
              <Row gutter={16}>
                <Col flex="0 1 280px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.fromAirportLabel)}
                    name={'airportFromId'}
                    rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                  >
                    <DebounceSelect
                      key={fromAirportFilter.key}
                      fetchOptions={fromAirportFilter.fetchOptions}
                      placeholder={intl.formatMessage(CommonFormMessages.fromAirportPlaceholder)}
                      showSearch={true}
                      defaultOptionsFetchCallback={getInitialAirportFromCallback}
                    />
                  </CustomFieldInput>
                </Col>

                <Col flex={1} style={{display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', paddingLeft: '8px', paddingRight: '8px'}}>
                  <PlaneGreyIcon />
                  <div style={{display: 'flex', alignItems: 'center', position: 'relative', width: '100%'}}>
                    <div style={{width: '100%', height: '2px', backgroundColor: '#808080', position: 'absolute'}}></div>
                    <div style={{width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#808080', position: 'absolute', right: 0}}></div>
                  </div>
                </Col>

                <Col flex="0 1 280px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.toAirportLabel)}
                    name={'airportToId'}
                    rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                  >
                    <DebounceSelect
                      key={toAirportFilter.key}
                      fetchOptions={toAirportFilter.fetchOptions}
                      placeholder={intl.formatMessage(CommonFormMessages.toAirportPlaceholder)}
                      showSearch={true}
                      defaultOptionsFetchCallback={getInitialAirportToCallback}
                    />
                  </CustomFieldInput>
                </Col>
              </Row>
            </Col>
          </>
        }

        <Col flex={1}>
          <div style={{marginBottom: '16px'}}>
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.dateRangeLabel)} (UTC)`}
              name={'dateRange'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DateAndTimeRangePicker
                format="DD.MM.YYYY"
                placeholder={[intl.formatMessage(CommonUnitMessages.from), intl.formatMessage(CommonUnitMessages.to)]}
                inputReadOnly={true} 
                placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                airportFromOffset={airportFromOffset}
                airportToOffset={airportToOffset}
                containerHeight="100%"
              />
            </CustomFieldInput>
            <div style={{color: '#808080', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <WarningIcon style={{width: '12px', height: '12px', fill: '#808080'}} />
              {intl.formatMessage(CommonFormMessages.airportHoursWarning)}
            </div>
          </div>
        </Col>

        {eventTypeRadioValue === ScheduleType.ORDER_REQUEST &&
          <>
            <Col flex={1}>
              <Row gutter={16}>
                <Col flex={1}>
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.refuelingCountLabel)}
                    name={'refuelingCount'}
                  >
                    <CustomInputNumber 
                      controls={true} 
                      placeholder={intl.formatMessage(CommonFormMessages.commonPlaceholder)}
                      style={{width: "100%"}}
                      min={0}
                    />
                  </CustomFieldInput>
                </Col>
                <Col flex="0 1 120px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.paxLabel)}
                    name={'pax'}
                    rules={[
                      {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                      {min: 1, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinPax)},
                      {max: 100, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMaxPax)}
                    ]}
                  >
                    <CustomInputNumber 
                      controls={true} 
                      placeholder={intl.formatMessage(CommonFormMessages.paxPlaceholder)}
                      style={{width: "100%"}}
                    />
                  </CustomFieldInput>
                </Col>
              </Row>
            </Col>
          </>
        }

        {eventTypeRadioValue === ScheduleType.EMPTY_LEG &&
          <>
            <Col flex={1}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.refuelingCountLabel)}
                name={'refuelingCount'}
              >
                <CustomInputNumber 
                  controls={true} 
                  placeholder={intl.formatMessage(CommonFormMessages.commonPlaceholder)}
                  style={{width: "100%"}}
                  min={0}
                />
              </CustomFieldInput>
            </Col>
          </>
        }

        <Col flex={1}>
          <CustomFieldInput
            name={'isPublic'}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage(CommonFormMessages.openForPublicSchedule)}
            </Checkbox>
          </CustomFieldInput>
        </Col>

        <CustomFieldInput>
          <CustomButton
            block
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
          >
            {intl.formatMessage(CommonButtonMessages.create)}
          </CustomButton>
        </CustomFieldInput>
      </Form>
    </CustomModal>
  );
};

